import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, ChevronDown, Info, Sparkles, ArrowUpRight, Zap, History } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { AuditDeltaBreakdown } from './AuditDeltaBreakdown';

const DEFAULT_LABELS = {
  headline: 'Headline',
  about: 'About',
  experience: 'Experience',
  skills: 'Skills',
  engagement_hooks: 'Engagement Hooks',
};
// Engagement is now surfaced as its own metric below the main breakdown, so
// the main category list excludes it.
const ORDER = ['headline', 'about', 'experience', 'skills'];

function formatAuditDate(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return null;
  }
}

function tier(value) {
  if (value >= 80) return { label: 'Strong', tone: 'success' };
  if (value >= 60) return { label: 'Solid', tone: 'info' };
  if (value >= 40) return { label: 'Needs work', tone: 'warning' };
  return { label: 'Weak', tone: 'destructive' };
}

function toneClasses(tone) {
  switch (tone) {
    case 'success':
      return 'bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30';
    case 'info':
      return 'bg-[hsl(var(--info))]/15 text-[hsl(var(--info))] border-[hsl(var(--info))]/30';
    case 'warning':
      return 'bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30';
    case 'destructive':
    default:
      return 'bg-destructive/15 text-destructive border-destructive/30';
  }
}

function ScoreDial({ value, label, testid, accent }) {
  const v = Math.max(0, Math.min(100, value || 0));
  const t = tier(v);
  return (
    <div className="flex flex-col items-center text-center" data-testid={testid}>
      <p className="text-xs font-mono-edge text-muted-foreground tracking-[0.16em] mb-2">
        {label}
      </p>
      <div className="relative">
        <svg width="132" height="132" viewBox="0 0 132 132" className="-rotate-90">
          <circle
            cx="66"
            cy="66"
            r="56"
            fill="none"
            stroke="hsl(var(--secondary))"
            strokeWidth="10"
          />
          <motion.circle
            cx="66"
            cy="66"
            r="56"
            fill="none"
            stroke={accent === 'potential' ? 'hsl(var(--success))' : 'hsl(var(--primary))'}
            strokeWidth="10"
            strokeLinecap="round"
            strokeDasharray={2 * Math.PI * 56}
            initial={{ strokeDashoffset: 2 * Math.PI * 56 }}
            animate={{ strokeDashoffset: 2 * Math.PI * 56 * (1 - v / 100) }}
            transition={{ duration: 0.9, ease: [0.2, 0.8, 0.2, 1] }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-display text-4xl font-bold leading-none" data-testid={`${testid}-value`}>
            {v}
          </span>
          <span className="font-mono-edge text-[10px] text-muted-foreground mt-0.5">/ 100</span>
        </div>
      </div>
      <Badge className={`mt-3 font-mono-edge text-[10px] border ${toneClasses(t.tone)}`}>
        {t.label}
      </Badge>
    </div>
  );
}

function CategoryRow({ name, current, potential, testid }) {
  const c = Math.max(0, Math.min(100, current || 0));
  const p = Math.max(0, Math.min(100, potential || 0));
  const delta = Math.max(0, p - c);

  return (
    <div className="space-y-1.5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium">{name}</p>
        <div className="flex items-baseline gap-2">
          <span className="font-mono-edge text-sm font-semibold" data-testid={`${testid}-current`}>{c}</span>
          <span className="text-muted-foreground text-xs">→</span>
          <span className="font-mono-edge text-sm font-semibold text-[hsl(var(--success))]" data-testid={`${testid}-potential`}>
            {p}
          </span>
          {delta > 0 && (
            <Badge className="font-mono-edge text-[10px] border bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30" data-testid={`${testid}-delta`}>
              +{delta}
            </Badge>
          )}
        </div>
      </div>
      {/* Two-stack bar: current solid + potential extension */}
      <div className="relative h-3 rounded-full bg-secondary overflow-hidden">
        <motion.div
          className="absolute inset-y-0 left-0 bg-[hsl(var(--success))]/30"
          initial={{ width: 0 }}
          animate={{ width: `${p}%` }}
          transition={{ duration: 0.85, ease: [0.2, 0.8, 0.2, 1], delay: 0.05 }}
        />
        <motion.div
          className="absolute inset-y-0 left-0 bg-primary rounded-full"
          initial={{ width: 0 }}
          animate={{ width: `${c}%` }}
          transition={{ duration: 0.75, ease: [0.2, 0.8, 0.2, 1] }}
        />
      </div>
    </div>
  );
}

function MethodAccordion({ method }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-5 pt-4 border-t border-border">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
        data-testid="profile-scores-method-toggle"
      >
        <Info size={12} />
        How is this calculated?
        <ChevronDown
          size={12}
          className="transition-transform"
          style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
        />
      </button>
      {open && (
        <div className="mt-3 text-xs text-muted-foreground space-y-1.5" data-testid="profile-scores-method-details">
          <p>
            We use a <span className="font-medium text-foreground">hybrid scoring engine</span>: deterministic
            rule checks (length, completeness, keywords, outcomes, hook quality) are blended 50/50 with an
            AI evaluation against top-performing profiles in your role and industry.
          </p>
          <p>
            <span className="font-medium text-foreground">Current</span> reflects your profile as submitted.
            {' '}
            <span className="font-medium text-foreground">Potential</span> reflects the score you would reach
            if you adopted the recommended headline, About, experience bullets, and skill ordering.
          </p>
          {method && <p className="font-mono-edge text-[10px] opacity-70">Method: {method}</p>}
        </div>
      )}
    </div>
  );
}

export default function ProfileScoreCard({ scoring, analysis }) {
  if (!scoring || !scoring.current || !scoring.potential) return null;
  const labels = scoring.labels || DEFAULT_LABELS;
  const cur = scoring.current.categories || {};
  const pot = scoring.potential.categories || {};
  const overallCurrent = scoring.current.overall ?? 0;
  const overallPotential = scoring.potential.overall ?? 0;
  const overallDelta = Math.max(0, overallPotential - overallCurrent);

  // Previous-audit delta (from /api/analyze new-audit lineage)
  const prevId = analysis?.previous_analysis_id;
  const prevScore = analysis?.previous_score;
  const prevAt = analysis?.previous_completed_at;
  const auditDelta = (typeof prevScore === 'number') ? overallCurrent - prevScore : null;
  const prevDateLabel = formatAuditDate(prevAt);

  // Engagement Readiness (standalone metric — not in overall)
  const engagementBlock = scoring.engagement_readiness || {
    current: cur.engagement_hooks ?? 0,
    potential: pot.engagement_hooks ?? 0,
    delta: Math.max(0, (pot.engagement_hooks ?? 0) - (cur.engagement_hooks ?? 0)),
  };

  return (
    <Card className="border-border overflow-hidden" data-testid="profile-score-card">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />
      <CardContent className="p-6">
        {/* Previous-audit comparison — collapsed by default so it doesn't
            visually compete with the primary current/potential score.
            Renders only when we actually have a previous full audit to
            compare against (never sources from the homepage free score). */}
        {auditDelta !== null && (
          <Collapsible className="mb-5" data-testid="profile-score-previous-audit-collapsible">
            <CollapsibleTrigger
              className={
                'w-full rounded-lg border px-3 py-2 flex items-center gap-2 text-left transition-colors hover:bg-secondary/50 group ' +
                (auditDelta > 0
                  ? 'bg-[hsl(var(--success))]/6 border-[hsl(var(--success))]/25'
                  : auditDelta < 0
                  ? 'bg-destructive/6 border-destructive/25'
                  : 'bg-secondary/40 border-border')
              }
              data-testid="profile-score-previous-audit-toggle"
            >
              <History size={13} className="text-muted-foreground shrink-0" />
              <span className="text-xs font-mono-edge tracking-[0.14em] uppercase text-muted-foreground">
                Compare to previous audit
              </span>
              <Badge
                variant="outline"
                className={
                  'ml-1 rounded-full text-[10px] font-mono-edge tracking-[0.14em] border ' +
                  (auditDelta > 0
                    ? 'text-[hsl(var(--success))] border-[hsl(var(--success))]/40'
                    : auditDelta < 0
                    ? 'text-destructive border-destructive/40'
                    : 'text-muted-foreground border-border')
                }
                data-testid="profile-score-previous-delta-badge"
              >
                {auditDelta > 0 ? `+${auditDelta}` : auditDelta < 0 ? `${auditDelta}` : '±0'}
                <span className="mx-1 opacity-50">·</span>
                {prevScore} → {overallCurrent}
              </Badge>
              <ChevronDown
                size={14}
                className="ml-auto text-muted-foreground transition-transform group-data-[state=open]:rotate-180"
                aria-hidden="true"
              />
            </CollapsibleTrigger>
            <CollapsibleContent
              className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-top-1 data-[state=closed]:slide-out-to-top-1"
              data-testid="profile-score-previous-audit-content"
            >
              {/* Detail banner shown only when the collapsible is expanded */}
              <div
                className={
                  'mt-3 rounded-lg border px-3 py-2 flex items-center gap-2 ' +
                  (auditDelta > 0
                    ? 'bg-[hsl(var(--success))]/8 border-[hsl(var(--success))]/30 text-[hsl(var(--success))]'
                    : auditDelta < 0
                    ? 'bg-destructive/8 border-destructive/30 text-destructive'
                    : 'bg-secondary border-border text-muted-foreground')
                }
                data-testid="profile-score-previous-delta"
              >
                <ArrowUpRight
                  size={14}
                  style={{ transform: auditDelta < 0 ? 'rotate(90deg)' : auditDelta === 0 ? 'rotate(45deg)' : 'none' }}
                />
                <span className="text-sm font-medium">
                  {auditDelta > 0
                    ? `+${auditDelta} since your previous full audit${prevDateLabel ? ` on ${prevDateLabel}` : ''}`
                    : auditDelta < 0
                    ? `${auditDelta} since your previous full audit${prevDateLabel ? ` on ${prevDateLabel}` : ''}`
                    : `No change since your previous full audit${prevDateLabel ? ` on ${prevDateLabel}` : ''}`}
                </span>
                <span className="text-xs text-muted-foreground ml-auto font-mono-edge">
                  {prevScore} → {overallCurrent}
                </span>
              </div>

              {/* Per-category & per-pillar delta breakdown — explains *why* the
                  overall moved up, down, or stayed flat. */}
              <div className="mt-3">
                <AuditDeltaBreakdown
                  analysis={analysis}
                  overallDelta={auditDelta}
                  prevScore={prevScore}
                  currentScore={overallCurrent}
                  prevDateLabel={prevDateLabel}
                />
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}

        <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
          <div>
            <p className="text-xs font-mono-edge text-primary tracking-[0.16em]">PROFILE SCORECARD</p>
            <h2 className="text-2xl font-semibold mt-1">
              Where you stand, and where you can land.
            </h2>
            <p className="text-sm text-muted-foreground mt-1 max-w-xl">
              Assessed by Blayzrr&apos;s objective-specific Profile Intelligence Framework. The potential score
              shows what&apos;s possible if you implement every recommendation below. Peer benchmarking is <span className="whitespace-nowrap">part of the Enterprise Plan.*</span>
            </p>
          </div>
          {overallDelta > 0 && (
            <Badge className="font-mono-edge text-xs border bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30 py-1.5 px-3" data-testid="profile-score-uplift">
              <TrendingUp size={12} className="mr-1.5" />
              +{overallDelta} pts of upside
            </Badge>
          )}
        </div>

        {/* Dials */}
        <div className="grid grid-cols-2 gap-4 sm:gap-8 max-w-md mx-auto sm:mx-0">
          <ScoreDial
            label="CURRENT"
            value={overallCurrent}
            testid="profile-score-current-overall"
            accent="current"
          />
          <ScoreDial
            label="POTENTIAL"
            value={overallPotential}
            testid="profile-score-potential-overall"
            accent="potential"
          />
        </div>

        {/* Category rows */}
        <div className="mt-8">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm font-semibold">Category breakdown</p>
            <div className="hidden sm:flex items-center gap-3 text-[11px] text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block w-3 h-2 rounded-full bg-primary" /> Current
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block w-3 h-2 rounded-full bg-[hsl(var(--success))]/30" /> Potential
              </span>
            </div>
          </div>
          <div className="space-y-4" data-testid="profile-score-categories">
            {ORDER.map((key) => (
              <CategoryRow
                key={key}
                name={labels[key] || key}
                current={cur[key]}
                potential={pot[key]}
                testid={`profile-score-row-${key}`}
              />
            ))}
          </div>
        </div>

        {/* Engagement Readiness — standalone metric (not in overall). */}
        <div
          className="mt-6 pt-5 border-t border-border"
          data-testid="profile-score-engagement-readiness"
        >
          <div className="flex items-center gap-2 mb-3">
            <Zap size={14} className="text-[hsl(var(--accent))]" />
            <p className="text-sm font-semibold">Engagement readiness</p>
            <Badge
              variant="outline"
              className="font-mono-edge text-[10px] text-muted-foreground border-border/70 ml-1"
            >
              SEPARATE
            </Badge>
            <span className="text-xs text-muted-foreground ml-auto hidden sm:block">
              Posting strength &mdash; not part of profile score
            </span>
          </div>
          <CategoryRow
            name={labels.engagement_hooks || 'Engagement hooks'}
            current={engagementBlock.current}
            potential={engagementBlock.potential}
            testid="profile-score-row-engagement-hooks"
          />
          <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">
            Reflects the strength of your recent posts (hooks, cadence, signal).
            Working on your profile copy first? Skip this for now &mdash; it doesn&apos;t
            drag your profile score down.
          </p>
        </div>

        {scoring.notes && (
          <div className="mt-6 rounded-lg border border-primary/20 bg-primary/5 p-3 flex items-start gap-2.5" data-testid="profile-score-notes">
            <Sparkles size={14} className="text-primary mt-0.5 shrink-0" />
            <p className="text-sm text-foreground/90">{scoring.notes}</p>
          </div>
        )}

        <MethodAccordion method={scoring.method} />
      </CardContent>
    </Card>
  );
}
